import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, Select, MenuItem, CircularProgress, Alert,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tooltip, Table, TableBody, TableCell, TableHead, TableRow, Skeleton,
  TableContainer,
} from "@mui/material";
import HistoryRoundedIcon         from "@mui/icons-material/HistoryRounded";
import RefreshRoundedIcon         from "@mui/icons-material/RefreshRounded";
import ReceiptLongRoundedIcon     from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleRoundedIcon     from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon           from "@mui/icons-material/ErrorRounded";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import { getData, API_ENDPOINTS }  from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

const fmtDate  = d => d ? new Date(d).toLocaleDateString("en-IN",   { day:"2-digit", month:"short", year:"numeric" }) : "—";
const fmtDateT = d => d ? new Date(d).toLocaleString("en-IN",       { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

// ── Micro chip ────────────────────────────────────────────────────────────
const MChip = ({ label, bg, color }) => (
  <Box sx={{ display:"inline-flex", alignItems:"center", gap:0.5, px:1.2, py:0.3, borderRadius:"8px", bgcolor:bg, border:`1px solid ${color}22` }}>
    <Box sx={{ width:5, height:5, borderRadius:"50%", bgcolor:color }} />
    <Typography sx={{ fontSize:11, fontWeight:700, color, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{label}</Typography>
  </Box>
);

// ── Filter pill ───────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value:"required_missing", label:"Required · EWB missing",         color:T.rose,    bg:T.roseLt    },
  { value:"required_entered", label:"Required · EWB entered",         color:T.emerald, bg:T.emeraldLt },
  { value:"not_required",     label:"Not required / exempt",          color:T.muted,   bg:"#F1F5F9"   },
  { value:"all",              label:"All",                            color:T.teal,    bg:T.tealLt    },
];

const SkelRow = () => (
  <TableRow>
    {[80,100,80,100,90,120,90,90,90,40].map((w,i)=><TableCell key={i}><Skeleton width={w} height={16} /></TableCell>)}
  </TableRow>
);

// ── Main component ────────────────────────────────────────────────────────
export default function EwbBookings() {
  const [status,  setStatus]  = useState("required_missing");
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [auditId, setAuditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getData(API_ENDPOINTS.ewbAdminBookings, { status, limit:100 });
      setRows(res?.result?.bookings || []);
    } catch(e) { setError(e.response?.data?.message || e.message); }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const activeCfg = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];

  // Summary counts
  const missingCount  = rows.filter(r => r.ewb_required && !r.ewb_number).length;
  const presentCount  = rows.filter(r => r.ewb_required && !!r.ewb_number).length;
  const exemptCount   = rows.filter(r => !r.ewb_required).length;

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero header ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <ReceiptLongRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>EWB Bookings</Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
              EWB-001 · Read-only list with status filter · click history icon for audit trail
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} sx={{ bgcolor:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:"10px",color:"#fff","&:hover":{ bgcolor:"rgba(255,255,255,0.25)" } }}>
            <RefreshRoundedIcon sx={{ fontSize:18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Toolbar ── */}
      <Box sx={{ display:"flex", gap:2, alignItems:"center", flexWrap:"wrap", mb:2.5 }}>
        {/* Status filter */}
        <Select value={status} onChange={e=>setStatus(e.target.value)} size="small" displayEmpty
          sx={{ borderRadius:"12px", bgcolor:"white", fontSize:13, minWidth:240 }}>
          {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value} sx={{ fontSize:13 }}>{o.label}</MenuItem>)}
        </Select>

        {/* Active filter pill */}
        <MChip label={activeCfg.label} bg={activeCfg.bg} color={activeCfg.color} />

        {/* Count pills */}
        {!loading && (
          <Box sx={{ display:"flex", gap:1.5, ml:"auto" }}>
            {status !== "not_required" && missingCount > 0 && (
              <Box sx={{ px:1.5,py:0.6,bgcolor:T.roseLt,borderRadius:"9px",border:`1px solid ${T.rose}22` }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:T.rose }}>{missingCount} missing</Typography>
              </Box>
            )}
            {status !== "not_required" && presentCount > 0 && (
              <Box sx={{ px:1.5,py:0.6,bgcolor:T.emeraldLt,borderRadius:"9px",border:`1px solid ${T.emerald}22` }}>
                <Typography sx={{ fontSize:12,fontWeight:700,color:T.emerald }}>{presentCount} present</Typography>
              </Box>
            )}
            <Box sx={{ px:1.5,py:0.6,bgcolor:T.bg,borderRadius:"9px",border:`1px solid ${T.border}` }}>
              <Typography sx={{ fontSize:12,fontWeight:600,color:T.muted }}>{rows.length} total</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb:2,borderRadius:"12px" }} onClose={()=>setError(null)}>{error}</Alert>}

      {/* ── Table ── */}
      <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:T.bg }}>
                {["Mapping","Trip","Tracking Code","Package","EWB Status","Required","Valid Until","GSTIN","Invoice","Created",""].map(h=>(
                  <TableCell key={h} sx={{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}`,whiteSpace:"nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({length:8}).map((_,i)=><SkelRow key={i} />)
                : rows.length===0
                ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py:6 }}>
                    <ReceiptLongRoundedIcon sx={{ fontSize:40,color:T.border,mb:1,display:"block",mx:"auto" }} />
                    <Typography fontSize={13} color="text.secondary">No bookings match this filter</Typography>
                  </TableCell></TableRow>
                )
                : rows.map(r => {
                  const hasEwb = !!r.ewb_number;
                  const isRequired = r.ewb_required === true;
                  const isExpired  = r.ewb_valid_until && new Date(r.ewb_valid_until) < new Date();

                  const ewbStatus = !isRequired
                    ? { label:"Exempt",  bg:"#F1F5F9",  color:T.muted   }
                    : !hasEwb
                    ? { label:"Missing", bg:T.roseLt,   color:T.rose    }
                    : isExpired
                    ? { label:"Expired", bg:T.amberLt,  color:T.amber   }
                    : { label:"Present", bg:T.emeraldLt,color:T.emerald };

                  return (
                    <TableRow key={r.mapping_id} hover sx={{ "&:last-child td":{ border:0 },"&:hover":{ bgcolor:T.tealLt+"33" } }}>

                      {/* Mapping # */}
                      <TableCell>
                        <Box sx={{ display:"inline-flex",px:1,py:0.3,bgcolor:T.bg,border:`1px solid ${T.border}`,borderRadius:"6px" }}>
                          <Typography sx={{ fontSize:11,fontWeight:700,color:T.muted,fontFamily:"monospace" }}>#{r.mapping_id}</Typography>
                        </Box>
                      </TableCell>

                      {/* Trip # */}
                      <TableCell>
                        <Box sx={{ display:"inline-flex",px:1,py:0.3,bgcolor:T.tealLt,border:`1px solid ${T.teal}22`,borderRadius:"6px" }}>
                          <Typography sx={{ fontSize:11,fontWeight:700,color:T.teal,fontFamily:"monospace" }}>#{r.trip_id||"—"}</Typography>
                        </Box>
                      </TableCell>

                      {/* Tracking code — NEW */}
                      <TableCell>
                        {r.Package?.tracking_code ? (
                          <Box sx={{ display:"inline-flex",px:1.2,py:0.3,bgcolor:T.indigoLt,border:`1px solid ${T.indigo}22`,borderRadius:"7px" }}>
                            <Typography sx={{ fontSize:11,fontWeight:700,color:T.indigo,fontFamily:"monospace" }}>{r.Package.tracking_code}</Typography>
                          </Box>
                        ) : <Typography sx={{ fontSize:12,color:T.muted }}>—</Typography>}
                      </TableCell>

                      {/* Package name */}
                      <TableCell>
                        <Typography sx={{ fontSize:12,color:T.text }}>{r.Package?.package_name || `Pkg #${r.package_id}`}</Typography>
                      </TableCell>

                      {/* EWB status chip */}
                      <TableCell>
                        <MChip label={ewbStatus.label} bg={ewbStatus.bg} color={ewbStatus.color} />
                        {hasEwb && (
                          <Typography sx={{ fontSize:10,color:T.muted,mt:0.3,fontFamily:"monospace" }}>{r.ewb_number}</Typography>
                        )}
                      </TableCell>

                      {/* Required */}
                      <TableCell>
                        {isRequired
                          ? <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.4,px:0.8,py:0.2,bgcolor:T.roseLt,borderRadius:"6px" }}>
                              <ErrorRoundedIcon sx={{ fontSize:12,color:T.rose }} />
                              <Typography sx={{ fontSize:10,fontWeight:700,color:T.rose }}>Required</Typography>
                            </Box>
                          : <Box sx={{ display:"inline-flex",alignItems:"center",gap:0.4,px:0.8,py:0.2,bgcolor:"#F1F5F9",borderRadius:"6px" }}>
                              <RemoveCircleOutlineRounded sx={{ fontSize:12,color:T.muted }} />
                              <Typography sx={{ fontSize:10,fontWeight:600,color:T.muted }}>{r.ewb_exempt_reason||"Exempt"}</Typography>
                            </Box>
                        }
                      </TableCell>

                      {/* Valid until */}
                      <TableCell>
                        <Typography sx={{ fontSize:12,color:isExpired?T.amber:T.muted }}>{fmtDate(r.ewb_valid_until)}</Typography>
                      </TableCell>

                      {/* GSTIN */}
                      <TableCell>
                        {r.consignor_gstin || r.consignee_gstin ? (
                          <Box>
                            {r.consignor_gstin && <Typography sx={{ fontSize:10,fontFamily:"monospace",color:T.muted }}>→ {r.consignor_gstin}</Typography>}
                            {r.consignee_gstin && <Typography sx={{ fontSize:10,fontFamily:"monospace",color:T.muted }}>← {r.consignee_gstin}</Typography>}
                          </Box>
                        ) : <Typography sx={{ fontSize:12,color:T.muted }}>—</Typography>}
                      </TableCell>

                      {/* Invoice */}
                      <TableCell>
                        <Typography sx={{ fontSize:11,color:T.muted }}>{r.invoice_number||"—"}</Typography>
                        {r.invoice_date && <Typography sx={{ fontSize:10,color:T.muted }}>{r.invoice_date}</Typography>}
                      </TableCell>

                      {/* Created */}
                      <TableCell><Typography sx={{ fontSize:12,color:T.muted }}>{fmtDate(r.createdAt)}</Typography></TableCell>

                      {/* Audit */}
                      <TableCell padding="none">
                        <Tooltip title="View audit log">
                          <IconButton size="small" onClick={()=>setAuditId(r.mapping_id)} sx={{ color:T.muted }}>
                            <HistoryRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* ── Audit dialog ── */}
      <AuditDialog mappingId={auditId} open={!!auditId} onClose={()=>setAuditId(null)} />
    </Box>
  );
}

// ── Audit dialog ──────────────────────────────────────────────────────────
function AuditDialog({ open, mappingId, onClose }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!open || !mappingId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const url = API_ENDPOINTS.ewbAdminBookingAudit.replace(":mapping_id", String(mappingId));
        const res = await getData(url);
        if (!cancelled) setEvents(res?.result?.events || []);
      } catch(e) {
        if (!cancelled) setError(e.response?.data?.message || e.message);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, mappingId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:"20px" } }}>
      <DialogTitle sx={{ fontWeight:800, fontSize:16, fontFamily:"'Plus Jakarta Sans',sans-serif", borderBottom:`1px solid ${T.border}`, pb:2 }}>
        Audit Log — Mapping #{mappingId}
      </DialogTitle>
      <DialogContent dividers sx={{ p:0 }}>
        {loading ? (
          <Box sx={{ display:"flex",justifyContent:"center",py:4 }}><CircularProgress sx={{ color:T.teal }} /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ m:2,borderRadius:"12px" }}>{error}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor:T.bg }}>
                {["When","Event","Field","Old Value","New Value","Actor"].map(h=>(
                  <TableCell key={h} sx={{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {events.length===0
                ? <TableRow><TableCell colSpan={6} align="center" sx={{ color:T.muted,py:4 }}>No events recorded</TableCell></TableRow>
                : events.map(e => (
                  <TableRow key={e.audit_id} hover sx={{ "&:last-child td":{ border:0 } }}>
                    <TableCell sx={{ fontSize:12,color:T.muted,whiteSpace:"nowrap" }}>{new Date(e.createdAt).toLocaleString("en-IN")}</TableCell>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",px:1,py:0.3,bgcolor:T.tealLt,borderRadius:"6px" }}>
                        <Typography sx={{ fontSize:11,fontWeight:700,color:T.teal,textTransform:"uppercase",letterSpacing:"0.3px" }}>{e.event_type}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize:12,color:T.muted }}>{e.field_name||"—"}</TableCell>
                    <TableCell><Typography sx={{ fontSize:11,fontFamily:"monospace",color:T.rose }}>{e.old_value??"∅"}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize:11,fontFamily:"monospace",color:T.emerald }}>{e.new_value??"∅"}</Typography></TableCell>
                    <TableCell sx={{ fontSize:12,color:T.muted }}>{e.actor_user_type}{e.actor_user_id?`:${e.actor_user_id}`:""}</TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5, borderTop:`1px solid ${T.border}` }}>
        <Button onClick={onClose} sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
